import React, { useEffect, useState } from 'react';
import Column from '@amzn/meridian/column';
import Row from '@amzn/meridian/row';
import Text from '@amzn/meridian/text';
import Link from '@amzn/meridian/link';
import Alert from '@amzn/meridian/alert';
import Icon from '@amzn/meridian/icon';
import Button from '@amzn/meridian/button';
import Expander from '@amzn/meridian/expander';
import copyTokens from '@amzn/meridian-tokens/base/icon/copy';
import checkCircleFilledTokens from '@amzn/meridian-tokens/base/icon/check-circle-filled';
import downloadLargeTokens from '@amzn/meridian-tokens/base/icon/download-large';
import refreshTokens from '@amzn/meridian-tokens/base/icon/refresh';
import infoTokens from '@amzn/meridian-tokens/base/icon/info';
import autofillDemo from '../../../images/autofill.gif';
import {
  LATEST_AUTOFILL_VERSION,
  compareVersions,
  detectAutofillVersionAsync,
} from '../../../utils/autofillVersion';
import {
  AUTOFILL_TUTORIALS,
  GETTING_STARTED_TUTORIALS,
  Tutorial,
  broadcastEmbedUrl,
  broadcastVideoUrl,
} from './tutorials';

/**
 * Tampermonkey's Chrome Web Store extension ID. Not something we can read from the page —
 * a web page cannot enumerate installed extensions — but we don't need to: the ID is
 * derived from the publisher's key, so every Web Store install gets this same one.
 */
const TAMPERMONKEY_CHROME_ID = 'dhdgffkkebhmkfjojejmpbldmpobfkfo';

const TAMPERMONKEY_LINKS = {
  chrome: `https://chromewebstore.google.com/detail/tampermonkey/${TAMPERMONKEY_CHROME_ID}`,
  firefox: 'https://addons.mozilla.org/en-US/firefox/addon/tampermonkey/',
};

/**
 * Deep link straight to Tampermonkey's own entry on the extensions page, where the
 * "Allow user scripts" toggle lives.
 *
 * Note for future edits: this is deliberately NOT rendered as an `<a href>`. Chrome refuses
 * to let a web page navigate to a `chrome://` URL — both a link click and `window.open` are
 * silently blocked — so it's offered as copyable text and the user pastes it.
 */
const TAMPERMONKEY_DETAILS_URL = `chrome://extensions/?id=${TAMPERMONKEY_CHROME_ID}`;

type PluginStatus = 'not-detected' | 'installed' | 'update-available';

function detectBrowser(): 'chrome' | 'firefox' | 'unknown' {
  const ua = navigator.userAgent.toLowerCase();
  if (ua.includes('firefox')) return 'firefox';
  if (ua.includes('chrome') || ua.includes('chromium')) return 'chrome';
  return 'unknown';
}

const AUTOFILL_INSTALL_URL = 'https://tamarin.aces.amazon.dev/scripts/navigator-autofill/install.user.js';

/**
 * The `chrome://` address as selectable monospace text with a copy-to-clipboard button,
 * since Chrome won't let us link to it. If the clipboard write is rejected (no permission,
 * insecure origin) the text stays selectable so the user can copy it by hand.
 */
const CopyableAddress: React.FC<{ url: string }> = ({ url }) => {
  const [copied, setCopied] = useState(false);
  const copy = () =>
    navigator.clipboard
      .writeText(url)
      .then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      })
      .catch(() => setCopied(false));

  return (
    <Row spacing="200" alignmentVertical="center">
      <code
        style={{
          background: '#f1f4f4',
          border: '1px solid #d5dbdb',
          borderRadius: '4px',
          padding: '4px 8px',
          fontSize: '13px',
          userSelect: 'all',
        }}
      >
        {url}
      </code>
      <Button
        type="icon"
        size="small"
        onClick={copy}
        label={copied ? 'Address copied' : 'Copy address'}
      >
        <Icon tokens={copyTokens} />
      </Button>
      {copied && (
        <Text type="b200" color="success">
          Copied
        </Text>
      )}
    </Row>
  );
};

const TutorialVideo: React.FC<{ tutorial: Tutorial }> = ({ tutorial }) => (
  <Column spacing="200">
    <Text type="b200">{tutorial.title}</Text>
    {/* 16:9 wrapper so the player scales with the page instead of using
        Broadcast's fixed 682x384 suggestion. */}
    <div style={{ position: 'relative', paddingBottom: '56.25%', height: 0 }}>
      <iframe
        src={broadcastEmbedUrl(tutorial.broadcastId)}
        title={tutorial.title}
        allowFullScreen
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          border: 0,
        }}
      />
    </div>
    {/* Fallback: the embed needs a live Midway session, which can lapse
        while the Navigator token is still valid. */}
    <Link
      href={broadcastVideoUrl(tutorial.broadcastId)}
      target="_blank"
      rel="noopener noreferrer"
    >
      Open in Broadcast
    </Link>
  </Column>
);

const ResourcesPage: React.FC = () => {
  const [detectedVersion, setDetectedVersion] = useState<string | null>(null);
  const [walkthroughOpen, setWalkthroughOpen] = useState(false);
  const [howItWorksOpen, setHowItWorksOpen] = useState(false);
  const [setupOpen, setSetupOpen] = useState(false);
  const [pluginVideosOpen, setPluginVideosOpen] = useState(false);
  const browser = detectBrowser();

  // Detect the installed userscript via the version marker it injects.
  useEffect(() => {
    let cancelled = false;
    detectAutofillVersionAsync().then((version) => {
      if (!cancelled) setDetectedVersion(version);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  // Only prompt an update when the installed version is genuinely older than the
  // latest — never nag a user who is on a newer build.
  const autofillStatus: PluginStatus = !detectedVersion
    ? 'not-detected'
    : compareVersions(LATEST_AUTOFILL_VERSION, detectedVersion) > 0
      ? 'update-available'
      : 'installed';

  const renderStatusBadge = () => {
    switch (autofillStatus) {
      case 'installed':
        return (
          <Row spacing="200" alignmentVertical="center">
            <Icon tokens={checkCircleFilledTokens} />
            <Text type="b200" color="success">Installed (v{detectedVersion})</Text>
          </Row>
        );
      case 'update-available':
        return (
          <Row spacing="200" alignmentVertical="center">
            <Icon tokens={refreshTokens} />
            <Text type="b200" color="warning">
              Update available: v{detectedVersion} → v{LATEST_AUTOFILL_VERSION}
            </Text>
          </Row>
        );
      default:
        return (
          <Row spacing="200" alignmentVertical="center">
            <Icon tokens={infoTokens} />
            <Text type="b200" color="secondary">Not detected</Text>
          </Row>
        );
    }
  };

  return (
    <div style={{ maxWidth: '900px', margin: '0 auto', padding: '32px 40px' }}>
      <Column spacing="500">
        <Text type="h300">Resources</Text>

        {/* First because it is where someone new to Navigator starts; everything below is
            the auto-fill plugin, which only matters once they have a session to report. */}
        <Column spacing="300">
          <Text type="h100">Getting Started</Text>
          <Text type="b200">
            Navigator end to end: session, consent, interview, validation.
          </Text>
          <Expander
            open={walkthroughOpen}
            onChange={setWalkthroughOpen}
            title="Watch the walkthrough"
            type="section"
          >
            <Column spacing="300" spacingInset="300 none none none">
              {GETTING_STARTED_TUTORIALS.map((tutorial) => (
                <TutorialVideo key={tutorial.broadcastId} tutorial={tutorial} />
              ))}
            </Column>
          </Expander>
        </Column>

        <Column spacing="300">
          {/* Status stays outside the expanders: it is the only live state on the page, and
              "am I set up?" should not need a click to answer. */}
          <Row spacing="300" alignmentVertical="center">
            <Text type="h100">Navigator Auto-Fill Tampermonkey Script</Text>
            {renderStatusBadge()}
          </Row>
          <Text type="b200">
            Fills AUSTIN incident report forms from your Navigator sessions.
          </Text>

          <Expander
            open={howItWorksOpen}
            onChange={setHowItWorksOpen}
            title="How it works"
            type="section"
          >
            <Column spacing="300" spacingInset="300 none none none">
              <Text type="b200">
                1. Create a session in Navigator and finish the investigation so Navigator collects all
                the required information needed for AUSTIN. Then use the &quot;Create Incident&quot; button on
                the session detail page to open AUSTIN with the session linked.
              </Text>
              <Text type="b200">
                2. Alternatively, open an AUSTIN incident report in another tab and use the plugin&apos;s
                Session History panel to manually select a session.
              </Text>
              <Text type="b200">
                3. As you navigate between pages in AUSTIN (case creation, initial information,
                encounter details), the plugin will show which fields can be filled. Click
                &quot;Auto-Fill from Navigator&quot; to populate them.
              </Text>
              {/* A GIF rather than the Broadcast walkthrough already on this page: it plays
                  inline with nothing to click and no sound, so the steps above are shown as
                  well as described. The full video stays under "Watch the tutorials". */}
              <img
                src={autofillDemo}
                alt="The plugin filling an AUSTIN incident report from a Navigator session"
                style={{ width: '100%', maxWidth: '420px', borderRadius: '8px', border: '1px solid #d5dbdb' }}
              />
            </Column>
          </Expander>

          {/* Steps 1-3 are one task, so they sit behind one expander rather than three */}
          <Expander
            open={setupOpen}
            onChange={setSetupOpen}
            title="Install and set up"
            type="section"
          >
            <Column spacing="400" spacingInset="300 none none none">
              <Column spacing="300">
                <Text type="h100">Step 1: Install Tampermonkey</Text>
                <Text type="b200">
                  Tampermonkey is a browser extension required to run the auto-fill plugin.
                  Install it for your browser:
                </Text>
                <Row spacing="400">
                  <Link href={TAMPERMONKEY_LINKS.chrome} target="_blank" rel="noopener noreferrer">
                    <Row spacing="200" alignmentVertical="center">
                      <Icon tokens={downloadLargeTokens} />
                      <span>Chrome / Edge{browser === 'chrome' ? ' (your browser)' : ''}</span>
                    </Row>
                  </Link>
                  <Link href={TAMPERMONKEY_LINKS.firefox} target="_blank" rel="noopener noreferrer">
                    <Row spacing="200" alignmentVertical="center">
                      <Icon tokens={downloadLargeTokens} />
                      <span>Firefox{browser === 'firefox' ? ' (your browser)' : ''}</span>
                    </Row>
                  </Link>
                </Row>
              </Column>

              {/* Chrome-only: the per-extension user-scripts permission */}
              {browser !== 'firefox' && (
                <Column spacing="300">
                  <Text type="h100">Step 2: Allow user scripts in Chrome</Text>
                  <Column spacing="100">
                    <Text type="b200">1. Open the Tampermonkey extension settings in Chrome:</Text>
                    <Column spacing="100" spacingInset="none 000 none 400">
                      <Text type="b200">
                        • Copy this address into your address bar and press Enter:
                      </Text>
                      <Column spacingInset="none 000 none 300">
                        <CopyableAddress url={TAMPERMONKEY_DETAILS_URL} />
                      </Column>
                      <Text type="b200">
                        • Or open the Extensions menu, choose <strong>Manage extensions</strong>, then{' '}
                        <strong>Details</strong> on Tampermonkey
                      </Text>
                    </Column>
                    <Text type="b200">
                      2. Turn on <strong>Allow user scripts</strong>.
                    </Text>
                    <Text type="b200">
                      3. Turn on <strong>Pin to toolbar</strong>.
                    </Text>
                  </Column>
                </Column>
              )}

              <Column spacing="300">
                <Text type="h100">
                  Step {browser === 'firefox' ? '2' : '3'}: Install Navigator Auto-Fill Script
                </Text>
                <Text type="b200">
                  With Tampermonkey installed and enabled, click the link below — Tampermonkey
                  will prompt you to install the script.
                </Text>
                <Row spacing="400">
                  <Link href={AUTOFILL_INSTALL_URL} target="_blank" rel="noopener noreferrer">
                    <Row spacing="200" alignmentVertical="center">
                      <Icon tokens={downloadLargeTokens} />
                      <span>
                        {autofillStatus === 'update-available'
                          ? `Update to v${LATEST_AUTOFILL_VERSION}`
                          : 'Install Script'}
                      </span>
                    </Row>
                  </Link>
                </Row>
                {autofillStatus === 'not-detected' && (
                  <Alert type="neutral" size="small">
                    If you have already installed the script, try refreshing this page.
                    The script must be enabled in Tampermonkey for detection to work — and on
                    Chrome, a newly installed script stays inactive until you enable
                    &quot;Allow User Scripts&quot; under Extensions &rarr; Tampermonkey &rarr;
                    Manage Extension. That is only needed once per browser.
                  </Alert>
                )}
              </Column>
            </Column>
          </Expander>

          <Expander
            open={pluginVideosOpen}
            onChange={setPluginVideosOpen}
            title="Watch the tutorials"
            type="section"
          >
            <Column spacing="300" spacingInset="300 none none none">
              {AUTOFILL_TUTORIALS.map((tutorial) => (
                <TutorialVideo key={tutorial.broadcastId} tutorial={tutorial} />
              ))}
            </Column>
          </Expander>
        </Column>
      </Column>
    </div>
  );
};

export default ResourcesPage;
